<script setup lang="ts">
import type { FieldSize } from '@/models/main';

import { computed, ref } from 'vue';
import _capitalize from 'lodash/capitalize';

import useField from '@/composables/useField';

export interface Props {
  modelValue?: string | null;
  label?: string;
  size?: FieldSize;
  readonly?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: undefined,
  size: 'medium',
  readonly: false,
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});

const sizeClass = computed(() => 'size' + _capitalize(props.size));
</script>

<template>
  <v-textarea
    v-model="fieldValue"
    :class="['g-v-text-field', $style.kTextareaField, $style[sizeClass]]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :label="props.label"
    :readonly="props.readonly"
    :disabled="props.disabled"
    data-testid="kTextareaField"
  >
  </v-textarea>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.kTextareaField {
  :global {
    textarea.v-field__input {
      min-height: 40px;
      font-size: 16px;
      line-height: 22px;
      padding: 10px 16px 7px 16px;
      mask-image: none;
      border: 0;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.sizeSmall {
  :global {
    textarea.v-field__input {
      min-height: 36px;
      font-size: 14px;
      line-height: 20px;
      padding: 9px 16px 7px 16px;
    }
  }
}

.sizeLarge {
  :global {
    textarea.v-field__input {
      min-height: 48px;
      font-size: 18px;
      line-height: 26px;
      padding: 12px 16px 9px 16px;
    }
  }
}
</style>
